import React, { FC, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import FeatherIcon from "feather-icons-react";

import {
  StyledListItemIcon,
  StyledListItemText,
  StyledMenuItem,
} from "../../ActionsDropdown.styled";

import { useImportUserFormConfig } from "@api";
import { useUserStore } from "@store/user";
import { useMultistepFormStore } from "@components/common/MultistepForm/MultistepForm.store";
import { BackdropLoader } from "@components/common";

interface FormConfigImport {}

const FormConfigImport: FC<FormConfigImport> = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const navigate = useNavigate();
  const {
    formSettings: {
      formBasics: { id: formId },
    },
  } = useMultistepFormStore();
  const {
    importUserFormConfigAsync,
    importUserFormConfigEnabled,
    isPendingImportUserFormConfig,
    isErrorImportUserFormConfig,
  } = useImportUserFormConfig(user, formId);

  const handleImport = async (): Promise<void> => {
    const input = document.createElement("input");

    input.type = "file";
    input.accept = "application/json,.json";

    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) return;

      const isJson =
        file.type === "application/json" ||
        file.name.toLowerCase().endsWith(".json");

      if (!isJson) {
        enqueueSnackbar(
          t(
            "formConfigImport.pleaseSelectJsonFile",
            "Please select a JSON file (.json).",
          ),
          {
            variant: "warning",
          },
        );

        return;
      }

      const result = await importUserFormConfigAsync({ formId, file });

      navigate(`/form-builder/${result.data}`);
    };

    // Trigger native file picker
    input.click();
  };

  useEffect(() => {
    if (isErrorImportUserFormConfig) {
      enqueueSnackbar(t("forms.errorImportingFormConfig"), {
        variant: "error",
      });
    }
  }, [isErrorImportUserFormConfig, enqueueSnackbar, t]);

  return (
    importUserFormConfigEnabled && (
      <>
        <StyledMenuItem
          onClick={handleImport}
          disabled={isPendingImportUserFormConfig}
        >
          <StyledListItemIcon>
            <FeatherIcon icon="upload" width="20" height="20" />
          </StyledListItemIcon>
          <StyledListItemText>{t("forms.importFormConfig")}</StyledListItemText>
        </StyledMenuItem>
        <BackdropLoader
          isVisible={isPendingImportUserFormConfig}
          label={t("forms.importingFormConfig")}
        />
      </>
    )
  );
};

export default FormConfigImport;
